'use client';

/**
 * API namespace `conferences` — V139-RECURRENT-ROOMS-FRONTEND.
 *
 * Wraps the 4 recurrence endpoints mounted on the backend sub-router at
 * `/api/conferences/:roomId/recurrence` (POST/PATCH/DELETE/GET). These
 * endpoints were added in PR #1597 (V138-RECURRENT-ROOMS-BACKEND).
 *
 *   POST   /conferences/:roomId/recurrence   → createRecurrence(roomId, body)
 *   PATCH  /conferences/:roomId/recurrence   → updateRecurrence(roomId, patch)
 *   DELETE /conferences/:roomId/recurrence   → cancelRecurrence(roomId)
 *   GET    /conferences/:roomId/recurrence   → getRecurrence(roomId)
 *
 * The legacy conference methods (`getConferences`, `createConference`,
 * `updateRoomSettings`, `uploadRoomBranding`, `getConference`) remain on the
 * top-level `api.*` surface (`userMethods` in `./user.ts`) for backward
 * compat — moving them would break existing imports across the app. This
 * namespace adds ONLY the recurrence methods, exposed as
 * `api.conferences.createRecurrence(...)`, etc.
 *
 * Anti-§6 (CM-19/CM-15): pure thin wrapper over `apiFetch`. NO business
 * logic. The backend controller
 * (`backend/src/features/conference/controllers/recurrence-controller.ts`)
 * is the source of truth for response shapes — the DTOs here mirror
 * `packages/shared/src/types/room-recurrence.ts` (re-exported via
 * `@profileia/shared`).
 *
 * Anti-P-041: 0 NEW `as any` in this file.
 * Anti-P-042: 0 NEW `console.*`.
 * Anti-P-013: errors propagate as `ApiClientError` (thrown by `apiFetch`).
 * Anti-P-382: reuses the EXISTING Room + RoomRecurrence backend models via
 *             the 4 endpoints — NO parallel mechanism on the FE.
 * Anti-P-383: file ≤500L target.
 *
 * Spec ref: Founder directive 2026-11-02 ("una sala recurrente tipo
 *           alarma — se abre sola en horario programado + se cierra sola
 *           después de duración configurada + cobra minutes por
 *           activación").
 */

import { apiFetch } from '../core/fetch-client';
import { ApiClientError } from '../core/error-handler';
import type {
  RoomRecurrence,
  RoomRecurrenceCreateInput,
  RoomRecurrencePatchInput,
} from '../core/shared-types';

/**
 * Build the canonical recurrence path for a given room.
 *
 * `encodeURIComponent` mirrors the existing `getConference` / `updateRoomSettings`
 * pattern (`user.ts:537,547,567`) so roomIds with special characters are
 * safely encoded. Anti-P-013: never inline string concatenation in callers.
 */
function recurrencePath(roomId: string): string {
  return `/conferences/${encodeURIComponent(roomId)}/recurrence`;
}

export const conferences = {
  /**
   * POST /conferences/:roomId/recurrence — create a new recurrence config
   * on the room.
   *
   * The backend rejects the request with 409 ROOM_RECURRENCE_EXISTS if the
   * room already has an active/paused recurrence (the user must PATCH or
   * DELETE first). Returns 201 + the new `RoomRecurrence` document.
   *
   * Auth: requireAuth (mounted on the backend route). The handler reads
   * `req.user.sub` as the ownerId + validates host authz against
   * `Room.hostUserId` in the service layer.
   *
   * Body: `RoomRecurrenceCreateInput` — `{ pattern, weekdays?, startHourLocal,
   * startMinuteLocal, timezone, durationMinutes }`.
   */
  createRecurrence(
    roomId: string,
    body: RoomRecurrenceCreateInput,
    signal?: AbortSignal,
  ): Promise<RoomRecurrence> {
    return apiFetch<RoomRecurrence>(recurrencePath(roomId), {
      method: 'POST',
      body: JSON.stringify(body),
      signal,
    });
  },

  /**
   * PATCH /conferences/:roomId/recurrence — partial update of the room's
   * recurrence config. At least one field must be present (the backend Zod
   * schema enforces this). Returns 200 + the updated `RoomRecurrence` doc.
   *
   * Common patches: change `startHourLocal`/`startMinuteLocal`, toggle
   * `status` between 'active' / 'paused' (canceled is terminal — use
   * `cancelRecurrence` instead), update `durationMinutes`.
   *
   * Auth: requireAuth + host authz (same as create).
   */
  updateRecurrence(
    roomId: string,
    patch: RoomRecurrencePatchInput,
    signal?: AbortSignal,
  ): Promise<RoomRecurrence> {
    return apiFetch<RoomRecurrence>(recurrencePath(roomId), {
      method: 'PATCH',
      body: JSON.stringify(patch),
      signal,
    });
  },

  /**
   * DELETE /conferences/:roomId/recurrence — soft-cancel the room's
   * recurrence (sets `status='canceled'`; cron skips it on next tick; the
   * dashboard hides it). Returns 204 No Content.
   *
   * Anti-P-395 (no inventar financial logic): no minutes refund is issued
   * by this endpoint — the recurrence is canceled going forward. Past
   * activations are NOT reversed (matches the backend
   * `roomRecurrenceService.cancelRecurrence` semantics).
   *
   * Auth: requireAuth + host authz (same as create).
   */
  cancelRecurrence(
    roomId: string,
    signal?: AbortSignal,
  ): Promise<void> {
    return apiFetch<void>(recurrencePath(roomId), {
      method: 'DELETE',
      signal,
    });
  },

  /**
   * GET /conferences/:roomId/recurrence — fetch the room's active/paused
   * recurrence config. Returns the `RoomRecurrence` document or `null` if
   * the room has no recurrence (the backend returns 404 RECURRENCE_NOT_FOUND
   * when no doc exists; this method returns `null` for caller convenience —
   * the API client's error middleware converts 404 into `null` here).
   *
   * Auth: requireAuth (mounted on the backend route). The handler reads
   * `req.user.sub` — non-host users can read the recurrence (the schedule is
   * public to participants) but cannot mutate it.
   *
   * Anti-P-041: typed return — `RoomRecurrence | null`, no `as any`.
   */
  async getRecurrence(
    roomId: string,
    signal?: AbortSignal,
  ): Promise<RoomRecurrence | null> {
    try {
      const doc = await apiFetch<RoomRecurrence>(recurrencePath(roomId), {
        method: 'GET',
        signal,
      });
      return doc;
    } catch (err) {
      // The backend returns 404 RECURRENCE_NOT_FOUND when the room has no
      // recurrence config — this is a NORMAL state (most rooms don't recur),
      // so we convert it to `null` instead of throwing. Other errors
      // (network, 401, 403, 500...) propagate as `ApiClientError`.
      if (err instanceof ApiClientError && err.status === 404) {
        return null;
      }
      throw err;
    }
  },
};
